import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const containerStyle = { maxWidth: 1100, margin: '40px auto', padding: 24, border: '1px solid #ddd', borderRadius: 10, background: '#fafafa' };
const btnSecondary = { padding: '10px 16px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 8 };
const btnInfo = { padding: '6px 12px', background: '#17a2b8', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginRight: 6 };
const btnDanger = { padding: '6px 12px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer' };

const subjectColors = { 
  english: { bg: '#007BFF', text: '#fff' }, 
  math: { bg: '#28a745', text: '#fff' }, 
  science: { bg: '#6f42c1', text: '#fff' } 
};
const RAW_BASE = process.env.REACT_APP_API_BASE || '/api';
const API_BASE = RAW_BASE.replace(/["']/g, '').replace(/\/+$/, '');

export default function FlowBuilderExams() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [bandFilter, setBandFilter] = useState('');
  
  // ✅ Inline Edit Name State
  const [editingNameId, setEditingNameId] = useState(null);
  const [newExamName, setNewExamName] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => { fetchExams(); }, []);

  useEffect(() => {
    let result = exams || [];
    if (levelFilter) result = result.filter(e => String(e.level) === levelFilter);
    if (bandFilter) result = result.filter(e => e.start_band === bandFilter);
    setFilteredExams(result);
  }, [levelFilter, bandFilter, exams]);

  const fetchExams = async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/admin/exams`);
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      console.log('✅ Fetched exams:', data);
      setExams(Array.isArray(data) ? data : []);
      setFilteredExams(Array.isArray(data) ? data : []);
    } catch (err) { 
      console.error('❌ Failed to fetch exams:', err);
      setError(`Unable to load exams: ${err.message}`); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleDelete = async (id, examName) => {
    if (!window.confirm(`Delete "${examName}"? This will also remove all assigned attempts and cannot be undone.`)) return;
    try {
      const res = await fetch(`${API_BASE}/admin/exams/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      setExams(prev => prev.filter(e => e.id !== id));
    } catch (err) { 
      setError(err.message); 
    }
  };

  const handleEditNameClick = (exam) => {
    setEditingNameId(exam.id);
    setNewExamName(exam.exam_name);
  };

  const handleSaveName = async (examId) => {
    if (!newExamName.trim()) return;
    setSavingName(true);
    try {
      const res = await fetch(`${API_BASE}/admin/exams/${examId}/name`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}` 
        },
        body: JSON.stringify({ exam_name: newExamName.trim() })
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to update name');
      }
      
      // Update local state instantly
      setExams(prev => prev.map(e => e.id === examId ? { ...e, exam_name: newExamName.trim() } : e));
      setEditingNameId(null);
    } catch (err) {
      alert('Failed to save exam name: ' + err.message);
    } finally {
      setSavingName(false);
    }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';
  const clearFilters = () => { setLevelFilter(''); setBandFilter(''); };
  const BANDS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>📚 Exam Builder</h2>
        <button onClick={() => navigate('/admin/dashboard')} style={btnSecondary}>← Back</button>
      </div>

      {error && <div style={{ padding: 12, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 15 }}>⚠️ {error}</div>}

      {/* 🔍 Filters */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20, padding: 12, background: '#f8f9fa', borderRadius: 8 }}>
        <span style={{ fontWeight: 500, fontSize: 14 }}>Filters:</span>
        <select style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }} value={levelFilter} onChange={e => setLevelFilter(e.target.value)}>
          <option value="">All Levels</option>
          {Array.from({length:12},(_,i)=>(i+1).toString()).map(l => <option key={l} value={l}>Level {l}</option>)}
        </select>
        <select style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 13 }} value={bandFilter} onChange={e => setBandFilter(e.target.value)}>
          <option value="">All Start Bands</option>
          {BANDS.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        {(levelFilter || bandFilter) && <button onClick={clearFilters} style={{ ...btnSecondary, padding: '6px 10px', fontSize: 13 }}>Clear</button>}
        <span style={{ fontSize: 13, color: '#666', marginLeft: 'auto' }}>Showing {filteredExams.length} exam(s)</span>
      </div>

      {/* 📋 Table Header */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 0.8fr 1.2fr 0.8fr 1.8fr', gap: 10, padding: '10px 15px', background: '#e9ecef', borderRadius: '8px 8px 0 0', fontWeight: 600, fontSize: 14 }}>
        <div>Exam Name</div>
        <div>Subject</div>
        <div>Level</div>
        <div>Config</div>
        <div>Questions</div>
        <div>Actions</div>
      </div>

      {loading && <p style={{ textAlign: 'center', padding: 30, color: '#666' }}>⏳ Loading exams...</p>}
      
      {!loading && filteredExams.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, background: '#fff', borderRadius: 8, border: '2px dashed #ccc', marginTop: 10 }}>
          <p style={{ color: '#666', marginBottom: 20, fontSize: 16 }}>
            {exams.length === 0 ? 'No exams created yet.' : 'No exams match your filters.'}
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/admin/flow-builder')} style={{ ...btnInfo, padding: '10px 20px', fontSize: 15 }}>
              + Create Flow Exam
            </button>
            <button onClick={() => navigate('/admin/math-flow-builder')} style={{ ...btnInfo, padding: '10px 20px', fontSize: 15, background: '#28a745' }}>
              + Create Math Diagnostic
            </button>
            <button onClick={() => navigate('/admin/science-flow-builder')} style={{ ...btnInfo, padding: '10px 20px', fontSize: 15, background: '#6f42c1' }}>
              + Create Science Diagnostic
            </button>
          </div>
        </div>
      )}

      {/* 📦 Exam List */}
      {!loading && filteredExams.map(exam => {
        const colors = subjectColors[exam.exam_topic] || { bg: '#6c757d', text: '#fff' };
        
        const isMathDiagnostic = exam.exam_topic === 'math';
        const isScience = exam.exam_topic === 'science';
        const isEnglish = exam.exam_topic === 'english';
        
        let configDisplay = '—';
        if (isMathDiagnostic || isScience) {
          const chapters = exam.diagnostic_config?.chapters;
          configDisplay = chapters ? `${chapters.length} Chapter(s)` : 'Dynamic';
        } else {
          configDisplay = `${exam.start_band || '—'} → ${exam.end_band || '—'}`;
        }
        
        return (
          <div key={exam.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 0.8fr 1.2fr 0.8fr 1.8fr', gap: 10, padding: '14px 15px', background: '#fff', borderBottom: '1px solid #eee', alignItems: 'center' }}>
            <div style={{ fontWeight: 500 }}>
              {editingNameId === exam.id ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input 
                    style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: 14, width: '100%', maxWidth: 250 }}
                    value={newExamName}
                    onChange={(e) => setNewExamName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveName(exam.id)}
                    autoFocus
                  />
                  <button onClick={() => handleSaveName(exam.id)} disabled={savingName} style={{ ...btnInfo, padding: '4px 8px', fontSize: 11 }}>
                    {savingName ? '...' : '💾'}
                  </button>
                  <button onClick={() => setEditingNameId(null)} style={{ ...btnSecondary, padding: '4px 8px', fontSize: 11 }}>
                    ✖
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span>{exam.exam_name}</span>
                  <button 
                    onClick={() => handleEditNameClick(exam)} 
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#007BFF', fontSize: 12, padding: 2 }}
                    title="Edit Name"
                  >
                    ✏️
                  </button>
                </div>
              )}
              <div style={{ fontSize: 11, color: '#888' }}>Created: {formatDate(exam.created_at)}</div>
            </div>
            
            <div><span style={{ padding: '3px 10px', background: colors.bg, color: colors.text, borderRadius: 12, fontSize: 11, fontWeight: 600, textTransform: 'capitalize' }}>{exam.exam_topic}</span></div>
            <div style={{ textAlign: 'center', fontWeight: 500 }}>Lvl {exam.level || '—'}</div>
            <div style={{ textAlign: 'center', fontSize: 13, color: '#555', fontWeight: 500 }}>{configDisplay}</div>
            <div style={{ textAlign: 'center', fontWeight: 500 }}>{exam.question_count || (isMathDiagnostic || isScience ? 'Dynamic' : 0)}</div>
            
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(isMathDiagnostic || isScience) ? (
                <button 
                  onClick={() => navigate(`/admin/flow-preview/${exam.id}`)} 
                  style={{ ...btnInfo, padding: '6px 10px', fontSize: 11, background: isScience ? '#6f42c1' : '#28a745' }}
                >
                  ⚙️ {isScience ? 'Science Config' : 'Math Config'}
                </button>
              ) : (
                <button 
                  onClick={() => navigate(`/admin/exam-preview/${exam.id}`)} 
                  style={{ ...btnInfo, padding: '6px 10px', fontSize: 11, background: isEnglish ? '#007BFF' : '#17a2b8' }}
                >
                  🔀 {isEnglish ? 'English Flow' : 'Branching'}
                </button>
              )}
              
              <button 
                onClick={() => handleDelete(exam.id, exam.exam_name)} 
                style={{ ...btnDanger, padding: '6px 10px', fontSize: 11 }}
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}